if (!customElements.get("cart-complementary-products")) {
  customElements.define(
    "cart-complementary-products",
    class CartComplementaryProducts extends HTMLElement {
      connectedCallback() {
        this.abortController = new AbortController();
        this.load();
      }

      disconnectedCallback() {
        this.abortController?.abort();
      }

      async load() {
        const productId = this.dataset.productId;
        const container = this.querySelector("[data-swiper-container]");

        if (!productId || !container) return;

        try {
          let products = await this.fetchProducts(productId, "complementary");

          // Shopify only auto-generates the "related" recommendation set.
          // Use it when no complementary products were configured manually.
          if (!products.length) {
            products = await this.fetchProducts(productId, "related");
          }

          if (!products.length) {
            this.hidden = true;
            return;
          }

          container.replaceChildren(
            ...products.map((product) => this.createSlide(product)),
          );
          this.hidden = false;
          await this.updateSlider();
        } catch (error) {
          if (error.name !== "AbortError") {
            console.error("Unable to load complementary cart products", error);
          }
          this.hidden = true;
        }
      }

      async fetchProducts(productId, intent) {
        const url = new URL(
          this.dataset.recommendationsUrl,
          window.location.origin,
        );
        url.searchParams.set("product_id", productId);
        url.searchParams.set("limit", this.dataset.limit || "5");
        url.searchParams.set("intent", intent);

        const response = await fetch(url, {
          signal: this.abortController.signal,
          headers: { Accept: "application/json" },
        });

        if (!response.ok) {
          throw new Error(`Recommendations request failed: ${response.status}`);
        }

        const { products = [] } = await response.json();
        return products;
      }

      createSlide(product) {
        const slide = document.createElement("div");
        const variant =
          product.variants?.find((item) => item.available) ||
          product.variants?.[0];
        const productUrl = product.url || `/products/${product.handle}`;
        const imageUrl =
          typeof product.featured_image === "string"
            ? product.featured_image
            : product.featured_image?.url || product.images?.[0] || "";
        const price = Number(variant?.price ?? product.price ?? 0);
        const discountPercent = Math.min(
          100,
          Math.max(0, Number(this.dataset.discountPercent) || 0),
        );
        const discountedPrice = Math.round(price * (1 - discountPercent / 100));
        const available = Boolean(variant?.available);
        const formId = `CartDrawerComplementary-${product.id}`;
        const addLabel = this.dataset.addLabel || "Add to cart";
        const soldOutLabel = this.dataset.soldOutLabel || "Sold out";
        const buttonLabel = available ? addLabel : soldOutLabel;
        const image = imageUrl
          ? `<a href="${this.escape(productUrl)}" aria-label="${this.escape(product.title)}">
              <picture class="lux-complementary__card-picture">
                <img
                  src="${this.escape(imageUrl)}"
                  alt="${this.escape(product.featured_image?.alt || product.title)}"
                  class="lux-complementary__card-image"
                  width="400"
                  height="400"
                  loading="lazy"
                >
              </picture>
            </a>`
          : "";
        const priceMarkup = discountPercent
          ? `<div class="lux-complementary__card-prices">
              ${this.bucksMoney(price, "lux-complementary__card-prices-original")}
              ${this.bucksMoney(discountedPrice, "lux-complementary__card-prices-discounted")}
            </div>
            <div class="lux-complementary__card-savings">
              <span>${this.bucksMoney(price - discountedPrice)} Ušteda</span>
            </div>`
          : `<div class="lux-complementary__card-no-discount">
              ${this.bucksMoney(price)}
            </div>`;

        slide.className =
          "lux-complementary__slide swiper-slide wt-slider__slide";
        slide.setAttribute("data-swiper-slide", "");
        slide.innerHTML = `
          <article class="lux-complementary__card">
            ${image}
            <div class="lux-complementary__card-body">
              <h3 class="lux-complementary__card-title">
                <a href="${this.escape(productUrl)}">${this.escape(product.title)}</a>
              </h3>
              <div class="lux-complementary__card-price">${priceMarkup}</div>
              <product-form class="lux-complementary__card-form-wrapper">
                <form
                  method="post"
                  action="${this.escape(this.dataset.cartAddUrl || "/cart/add")}" 
                  id="${this.escape(formId)}"
                  class="wt-product__add-to-cart_form form lux-complementary__card-form"
                  data-type="add-to-cart-form"
                >
                  <div class="wt-product__add-to-cart_form--wrapper">
                    <input type="hidden" name="id" value="${this.escape(variant?.id || "")}">
                    <button
                      aria-label="${this.escape(buttonLabel)}"
                      id="ProductSubmitButton-${this.escape(formId)}"
                      type="submit"
                      name="add"
                      data-is-product-available="${available}"
                      class="product-form__submit lux-complementary__card-submit"
                      ${available ? "" : "disabled"}
                    >
                      <span class="product-button-pricing__wrapper">
                        <span class="product-button-pricing">
                          <span class="product-button-pricing__label">${this.escape(buttonLabel)}</span>
                        </span>
                      </span>
                      <span class="loading-overlay__spinner hidden">
                        <span class="lds-ring" aria-hidden="true">
                          <span></span><span></span><span></span><span></span>
                        </span>
                      </span>
                    </button>
                  </div>
                </form>
                <div class="product-form__error-message-wrapper">
                  <p class="product-form__error-message"></p>
                </div>
              </product-form>
            </div>
          </article>`;

        return slide;
      }

      async updateSlider() {
        await customElements.whenDefined("slideshow-section");
        await new Promise((resolve) => requestAnimationFrame(resolve));

        const slider = this.querySelector("slideshow-section");
        slider?.swiper?.updateSize();
        slider?.swiper?.updateSlides();
        slider?.swiper?.update();
      }

      bucksMoney(cents, additionalClass = "") {
        const baseValue = (Number(cents) || 0) / 100;
        const baseAmount = baseValue.toFixed(2);
        const formattedBaseAmount = new Intl.NumberFormat("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(baseValue);
        const classes = [
          additionalClass,
          "money",
          "buckscc-money",
          "buckscc-converted",
        ]
          .filter(Boolean)
          .join(" ");

        return `<span
          class="${this.escape(classes)}"
          bucks-original="${baseAmount} RSD"
          bucks-init="${baseAmount}"
          bucks-currency="RSD"
          bucks-current=""
        >${formattedBaseAmount} RSD</span>`;
      }

      escape(value) {
        return String(value ?? "")
          .replaceAll("&", "&amp;")
          .replaceAll('"', "&quot;")
          .replaceAll("'", "&#039;")
          .replaceAll("<", "&lt;")
          .replaceAll(">", "&gt;");
      }
    },
  );
}
